import React, { useCallback, useMemo } from 'react';
import { SearchInputRecord } from './ResourceListSearchFilters';
import { getIntl } from '../../../utils/intl';
import { NativeScrollEvent, NativeSyntheticEvent, Platform, StyleSheet, View } from 'react-native';
import { mainContentPadding } from '../../v2BaseComponents/MainContentContainer';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import { getSpacing } from '../../../utils/spacing';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import Text from '../../v2BaseComponents/Text';
import { PRFlatList, useIsInScrollView } from '../Scroll';

const intl = getIntl('SearchResultsList', {
    pastVisits: 'Past Visits',
    futureVisits: 'Future Visits',
});

export type SearchResultsListOwnProps = {
    isLoadingResources: boolean;
    disableFlatListPadding?: boolean;
    filteredResources: SearchInputRecord[];
    renderFilteredResources: (resources: SearchInputRecord) => React.ReactElement;
    onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
    renderBefore?: () => React.ReactElement | null;
};

export type SearchResultsListProps = SearchResultsListOwnProps;

const styles = StyleSheet.create({
    defaultDivider: {
        width: '100%',
        height: contentStackSpacing,
    },
    stackStyle: {
        marginTop: getSpacing(1),
        flex: 1,
    },
    flatListContentContainer: {
        padding: mainContentPadding,
    },
    flatListStyle: {
        width: '100%',
        flex: 1,
    },
});

const SearchListDivider = ({
    prevItem,
    nextItem,
}: {
    prevItem: SearchInputRecord | null;
    nextItem: SearchInputRecord | null;
}) => {
    const defaultDivider = <View style={styles.defaultDivider} />;

    if (!prevItem || !nextItem) return defaultDivider;

    if (prevItem.type !== 'visit' || nextItem.type !== 'visit') return defaultDivider;

    if (!prevItem.startDateTime || !nextItem.startDateTime) return defaultDivider;

    const prevTime = new Date(prevItem.startDateTime).getTime();
    const nextTime = new Date(nextItem.startDateTime).getTime();
    const curTime = new Date().getTime();

    if (prevTime > curTime && nextTime < curTime) {
        return (
            <Stack horizontal fullWidth padding={getSpacing(0.25)} align="center" justify="center">
                <Text variant="body-1">{intl('pastVisits')}</Text>
            </Stack>
        );
    } else if (prevTime < curTime && nextTime > curTime) {
        return (
            <Stack horizontal fullWidth padding={getSpacing(0.25)} align="center" justify="center">
                <Text variant="body-1">{intl('futureVisits')}</Text>
            </Stack>
        );
    } else {
        return defaultDivider;
    }
};

const SearchResultsList: React.FC<SearchResultsListProps> = (props) => {
    const isInScrollView = useIsInScrollView();

    const renderItem = useCallback(
        ({ item }: { item: SearchInputRecord }) => props.renderFilteredResources(item),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.renderFilteredResources],
    );

    const ItemSeparatorComponent = useMemo(
        () =>
            props.disableFlatListPadding
                ? undefined
                : ({ leadingItem }: { leadingItem?: { id: string } }) => {
                      const leadingIndex = props.filteredResources.findIndex(
                          (resource) => resource.id === leadingItem?.id,
                      );
                      const trailingIndex = leadingIndex + 1;

                      return (
                          <SearchListDivider
                              prevItem={props.filteredResources[leadingIndex] || null}
                              nextItem={props.filteredResources[trailingIndex] || null}
                          />
                      );
                  },
        [props.disableFlatListPadding, props.filteredResources],
    );

    const keyExtractor = useCallback((item: SearchInputRecord) => item.id, []);

    return (
        <AnimateLoadedContent isLoading={props.isLoadingResources} style={styles.stackStyle}>
            {() => {
                if (isInScrollView) {
                    return (
                        <Stack vertical space={contentStackSpacing}>
                            {props.filteredResources.map((resource) => (
                                <React.Fragment key={resource.id}>
                                    {props.renderFilteredResources(resource)}
                                </React.Fragment>
                            ))}
                        </Stack>
                    );
                } else {
                    return (
                        <PRFlatList
                            onScroll={props.onScroll}
                            scrollEventThrottle={12}
                            testID="search-results-list"
                            ListHeaderComponent={props.renderBefore}
                            contentContainerStyle={styles.flatListContentContainer}
                            style={styles.flatListStyle}
                            ItemSeparatorComponent={ItemSeparatorComponent}
                            data={props.filteredResources}
                            renderItem={renderItem}
                            keyExtractor={keyExtractor}
                            removeClippedSubviews={Platform.OS !== 'web'}
                            windowSize={Platform.OS === 'web' ? 10 : 5}
                        />
                    );
                }
            }}
        </AnimateLoadedContent>
    );
};

export default React.memo(SearchResultsList);
